import {useCallback, useEffect, useMemo, useState, type ReactNode} from 'react';
import type {MoneyTone} from '@pokpok/astryx';
import {ApiError, errorMessageKey} from '../../api';
import type {Order, OrderLine, PaymentSummary} from '../../api/types';
import {useRouter} from '../../app/router';
import {ROUTES} from '../../app/routes';
import {formatBasisPoints, useI18n} from '../../i18n';
import {isVerifiedPaid, orderTotalTone, paymentTone, useOrder} from '../../order';
import {useGuestSession} from '../../session';
import {useGuestStream, useStreamEvents} from '../../stream';
import {
  ActionLink,
  BackLink,
  Button,
  Card,
  Money,
  Screen,
  ScreenBody,
  ScreenFooter,
  SectionHeader,
  SkeletonBar,
  SkeletonRow,
  SplitRow,
  Stack,
  StatusPill,
  TerminalNotice,
  TopBar,
  cx,
} from '../../ui';
import {LanguageToggle} from '../Q02LanguageToggle';
import './q17.css';

/**
 * # Q17 · Order confirmation and receipt
 *
 * Route `/order/:orderId`. The guest's proof, and the one screen whose whole job
 * is to still be there later.
 *
 * ## Why it re-reads instead of being handed the order
 *
 * Nothing about this screen is passed through navigation state. The order id is
 * in the URL and the order is fetched from the server on mount, because the
 * three cases the spec calls out — a page refresh, an app switch, a guest coming
 * back an hour later — all destroy in-memory state and none of them destroy the
 * URL. A lost confirmation is a support call (03 §Q17), and a support call is
 * the exact cost the QR surface exists to avoid.
 *
 * It also re-reads on three signals after that: a stream frame naming this
 * order, a `stream.reset` (the replay buffer moved past us, so every cached
 * snapshot is suspect), and the tab becoming visible again. The last one is the
 * real-world case: the guest walks to the counter with the page open, pays, and
 * comes back to the tab. That is the moment the ticket turns green, and it must
 * turn green **because the server said so** — not because a phone assumed the
 * walk succeeded.
 *
 * ## The money rule, on the one screen most tempted to break it
 *
 * Pandan appears if and only if `orderTotalTone` / `paymentTone` say `verified`
 * (foundations §4, D-021). Everything else on this page — subtotal, service
 * charge, VAT, rounding, an outstanding balance — is `neutral` or `pending`, and
 * an unpaid ticket says so in plain words (`q17.unpaid`, `q17.payAtCounter`)
 * with no colour carrying the meaning on its own. There is no "probably paid"
 * branch here because there is no such tone. **Not one green on this screen is
 * typed by hand.** Every `Money` tone that could be Pandan is a call into
 * `src/order/moneyTone.ts`, and the one hand-written `tone="verified"` — the
 * paid pill — sits inside a branch gated on `isVerifiedPaid(order)`, which is
 * that same call. Literals pointing the other way (`tone="neutral"` on a settled
 * total) are not the hazard the rule guards against and are used where the
 * routed call would only re-derive a constant; the green is the server's word or
 * it is absent.
 *
 * `q17.processorDisclosure` renders **only when a payment that ReservePay
 * actually processed exists** — never on a cash bill, where it would be false,
 * and never on an unpaid one, where there is nothing to disclose. It is body
 * copy at the foot of the payment block, not a lockup, and it is nowhere near
 * the wordmark's position (D-014, foundations §7).
 *
 * ### Where the green goes: one word, not a card (2026-08-14)
 *
 * A settled ticket used to spend the green three times over — a Pandan ground
 * and edge on the whole card, *plus* the big figure tinted. The design package
 * spends it once: the card stays white on Sticky Rice like every other card, the
 * figure stays ink like every other figure, and the only Pandan on the screen is
 * the word `q17.paid` in its pill (prototype line 241, where the ground is
 * `#FFFFFF` unconditionally and the 26px amount is `#1A1D23` unconditionally —
 * only the status label takes a colour, and it takes it from a variable).
 *
 * Owner ruled on 2026-08-14 to follow the package, and it **sharpens** the money
 * rule rather than weakening it. Green still appears if and only if ReservePay
 * verified the money; what changed is that it now lands on the phrase making the
 * claim instead of washing everything within the card's border, half of which —
 * an order number, a timestamp, a staleness hint — is not a claim about money at
 * all. A tint that covers everything says nothing in particular; a tinted word
 * beside an ink figure is legible *as a claim*, which is the only reason the
 * colour is reserved.
 *
 * The consequence to accept: at a glance across the room the paid state is now a
 * smaller signal than it was. That is the right trade on a receipt, which is read
 * held in the hand, and the state is also carried in words (the pill's own
 * label), in the `aria-label` on the figure, and in the settlement group below —
 * so nothing depended on the wash.
 *
 * ## One figure per line in the money column (2026-08-13)
 *
 * The receipt used to print each modifier's price delta under its line — a line
 * at ฿90 with `฿15` sitting beneath it — and `line_total_satang` **already
 * contains that ฿15**. Two figures in the money column, one inside the other,
 * and the guest is invited to add them: the same double-counting defect ADR-0009
 * names for inclusive VAT, one level down. Both readings are closed here:
 *
 *   * A line prints exactly one amount, the one the venue charged for it. The
 *     modifiers are still listed, by name, because a guest checking a receipt is
 *     checking *what arrived*; what an extra cost was a decision they already
 *     made on Q4, where the delta was shown beside the choice.
 *   * The VAT row is a **note under the total, never a row added into it**, when
 *     `vat_mode` is `inclusive` — see `Ledger`.
 *
 * ## The shell (redesign, 2026-08-13)
 *
 * The design package draws the summary as a single scrolling block with **no
 * pinned footer**: the only affordance is inside the money card, because nothing
 * on a bill is an action the guest came to take. So `ScreenBody` takes no
 * `scroll` — the document scrolls, which is what collapses the browser's URL bar
 * and is real vertical space on a phone (foundations §12).
 *
 * ## No photos, by construction
 *
 * There is no image on this screen at all — not a hidden one, not a placeholder
 * with a fallback. A receipt is a list of names, quantities and money, which is
 * exactly what the merchant with no photography has. The no-photo case is not a
 * branch here; it is the only rendering.
 *
 * ## An item that vanishes
 *
 * The menu projection is irrelevant to a receipt — lines carry `name_snapshot`,
 * so a rename or an 86 after the fact must never rewrite what a guest ordered.
 * The receipt's version of "it disappeared" is a line the venue **voided or the
 * kitchen cancelled**, and those are kept on the page, struck through and
 * labelled, never silently dropped. A guest whose dish quietly vanishes from
 * their own receipt cannot tell a mistake from a memory, and that is a dispute
 * at the counter.
 *
 * ## Deliberately absent
 *
 * No tax-invoice request flow: the e-Tax engine is V2 and there is no endpoint
 * for it, so it points at the counter (`q17.taxInvoiceAtCounter`).
 *
 * **No payment flow, and no door to one.** This said "checkout is PLACEHOLDER
 * (TBD)" until 2026-08-12, then carried a `q17.payOnline` link to Q8 until
 * 2026-08-14, when the owner took it out: for now the surface is an ordering
 * system and money is settled at the counter or by asking a member of staff.
 * `q17.payAtCounter` is the whole of what this screen says about paying, and it
 * is true for every venue. See the comment at the payment block for why the
 * shorter claim is also the more honest one.
 */

// --- screen ----------------------------------------------------------------

export default function Q17OrderConfirmation() {
  const {t, money, time} = useI18n();
  const {params} = useRouter();
  const {session, status: sessionStatus} = useGuestSession();
  const {loadOrder, order: liveOrder} = useOrder();
  const {resetNonce, stale} = useGuestStream();

  const orderId = params['orderId'] ?? '';
  const sessionToken = session?.session_token ?? null;

  const [fetched, setFetched] = useState<Order | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!orderId || !sessionToken) return;
    let cancelled = false;
    setLoading(true);
    loadOrder(orderId)
      .then((next) => {
        if (cancelled) return;
        setFetched(next);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        // Deliberately does **not** clear `fetched`. A receipt already on screen
        // must survive the phone walking out of coverage; blanking it because a
        // background re-read timed out is how a guest loses their proof while
        // standing at the till.
        setError(
          caught instanceof ApiError
            ? caught
            : new ApiError({kind: 'parse', message: String(caught)}),
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orderId, sessionToken, loadOrder, nonce, resetNonce]);

  // A pushed frame is a hint to re-read, never truth — least of all about money.
  useStreamEvents((event) => {
    if (!event.order_id || event.order_id !== orderId) return;
    if (fetched && event.order_seq !== null && event.order_seq !== undefined) {
      if (fetched.seq >= event.order_seq) return;
    }
    reload();
  });

  // The guest walked to the counter, paid, and came back to this tab.
  useEffect(() => {
    const doc = globalThis.document;
    if (!doc) return;
    const onVisibility = () => {
      if (doc.visibilityState === 'visible') reload();
    };
    doc.addEventListener('visibilitychange', onVisibility);
    return () => doc.removeEventListener('visibilitychange', onVisibility);
  }, [reload]);

  // `OrderProvider` keeps the table's active order fresh off the stream. When
  // that is this order, prefer whichever copy is further ahead — `seq` is the
  // order's own version, so this cannot go backwards.
  const order = useMemo<Order | null>(() => {
    if (liveOrder && liveOrder.order_id === orderId) {
      if (!fetched || liveOrder.seq >= fetched.seq) return liveOrder;
    }
    return fetched;
  }, [liveOrder, fetched, orderId]);

  if (!order) {
    if (!orderId) return <Notice title={t('q17.notFound')} body={t('q17.notFoundAction')} />;

    // No credential at all: the QR was never scanned, or it expired. Say what to
    // do, in one line, and do not fire a request that can only 401.
    if (!sessionToken && sessionStatus !== 'opening') {
      return <Notice title={t('error.session')} body={t('common.askStaff')} />;
    }

    if (error) {
      const notFound = error.code === 'not_found' || error.status === 404;
      return (
        <Notice
          title={notFound ? t('q17.notFound') : t(errorMessageKey(error))}
          body={notFound || error.isSessionProblem ? t('q17.notFoundAction') : undefined}
          onRetry={!notFound && error.retriable ? reload : undefined}
          retryLabel={t('common.retry')}
        />
      );
    }

    return <ReceiptSkeleton busy={loading} />;
  }

  const totals = order.totals;
  const paid = isVerifiedPaid(order);
  const payments = order.payments ?? [];
  const verifiedPayments = payments.filter((p) => paymentTone(p) === 'verified');
  const paidAt = latestPaymentTime(verifiedPayments);
  /*
   * Rendered times, not timestamps. Empty when the merchant's zone is unknown —
   * `formatTime` refuses to guess and this surface must never fall back to the
   * device's zone (C-4, D-023). Resolved once, here, so every label below can
   * ask "is there a time" rather than each deciding again.
   */
  const placedAt = time(order.created_at);
  const paidAtTime = paidAt ? time(paidAt) : '';
  const updatedAt = time(order.updated_at);
  const refundedTotal = totals.refunded_total_satang;
  const balanceDue = totals.balance_due_satang;
  const itemCount = order.lines
    .filter((line) => !isLineDropped(line))
    .reduce((sum, line) => sum + line.quantity, 0);
  const ticketOpen = order.state === 'open' || order.state === 'draft';

  // Factual only where a payment ReservePay actually processed exists. Cash at
  // the counter is not processed by anybody, and saying otherwise is a false
  // statement on a receipt.
  const processorDisclosed = payments.some(
    (p) =>
      p.method !== 'cash' &&
      (p.state === 'succeeded' || p.state === 'refunded' || p.state === 'partially_refunded'),
  );

  return (
    <Screen>
      {/*
        The design gives the summary a back affordance and the language control,
        and nothing else in the bar. Back goes where the guest came from in the
        one case the flow guarantees — the open ticket's status screen — and to
        the menu otherwise, because a settled or voided order has no live status
        to return to and a link to a finished progress list is a dead end dressed
        as an action. With no credential at all there is nowhere to send anybody,
        so the slot stays empty rather than offering a link that will 401.

        INTEGRATION · Q17 shipped with no way to change language, and it is the
        one screen a guest may reopen hours later on a phone whose stored choice
        is gone. Foundations §8: the switcher is reachable from every screen.
      */}
      <TopBar
        leading={
          sessionToken ? (
            <BackLink to={ticketOpen ? ROUTES.status : ROUTES.menu} label={t('common.back')} />
          ) : null
        }
        trailing={<LanguageToggle variant="link" />}
      />

      <ScreenBody>
        <div className="q17">
          <header className="q17-header">
            {/*
             * `q17.thanks` — an eyebrow line above the title — stood here until
             * 2026-08-14 and is gone. It arrived in the redesign with nothing
             * written for it, the package opens straight on the title, and three
             * things went against it once anybody looked:
             *
             *   * **It is not always true.** This header is fixed and the card
             *     under it is not: the same screen renders an unpaid balance, a
             *     refund and a venue-voided ticket. Thai `ขอบคุณ` survives that
             *     reasonably; zh `谢谢惠顾` is thanks *for the custom* and reads
             *     as a completed sale, which above `q14.orderVoided` is the page
             *     contradicting itself in two lines.
             *   * **It costs the guest the one fact they opened the page for.**
             *     Q17 is entered by a guest asking "what do I owe" or "what did I
             *     order", often hours later and often from the status screen. The
             *     title and the order number are the answer; a courtesy above
             *     them is a word to scroll past on the smallest viewport.
             *   * **The skeleton never drew it**, so the header grew by a line
             *     the moment the order landed — a reflow in the block whose whole
             *     job is to be stable, and the thing `ReceiptSkeleton`'s comment
             *     claims not to do. Dropping the line makes that claim true
             *     rather than adding a grey bar to keep a courtesy company.
             *
             * The key stays in all three dictionaries, unused, like
             * `q17.keepThisPage` — it is good copy in the wrong slot, and the
             * place it would earn is a settled-only line beside `q17.paidAt`, not
             * an unconditional one above the title.
             */}
            <h1 className="pk-title">{t('q17.title')}</h1>
            {/* The design draws one 12.5px subtitle line carrying both facts. At
                the 16px floor the hierarchy inside it has to be weight and
                colour instead of size: the order number is the thing a guest
                reads out at the counter, so it keeps ink and 600.

                Both halves are optional and the line disappears with them. A
                dine-in order carries no `order_number` at all (it is minted for
                queue-ahead and takeaway only), and `time()` returns `''` rather
                than guessing when the merchant's zone is unknown — so the empty
                string is a real value here, not a defensive check. Rendering the
                label around it printed a bare "สั่งเมื่อ" with nothing after it,
                which reads as a page that failed rather than as a fact nobody
                has. */}
            {order.order_number || placedAt ? (
              <p className="q17-subtitle">
                {order.order_number ? (
                  <span className="pk-strong">
                    {t('q17.orderNumber', {number: order.order_number})}
                  </span>
                ) : null}
                {order.order_number && placedAt ? (
                  <span className="pk-hint" aria-hidden="true">
                    ·
                  </span>
                ) : null}
                {placedAt ? (
                  <span className="pk-muted">{t('q17.placedAt', {time: placedAt})}</span>
                ) : null}
              </p>
            ) : null}
          </header>

          {/* The one thing the guest came here to know. `role="status"` so a
              payment landing while the page is open is announced, not just
              repainted.

              The card is `default` in every state, settled included — see "Where
              the green goes" above. `Card tone="verified"` is not wrong here so
              much as spent in the wrong place: it is the right primitive for a
              card that *is* the verification, and this one is the bill, of which
              the verification is one line. */}
          {/*
            ## The bill's one card, re-laid-out 2026-08-14 (owner directive)

            It was a plain vertical stack — pill, amount, instruction, updated,
            stale — five left-aligned lines of descending importance, which read
            as a list rather than as a bill. Losing the pay-online link left it
            with nothing to end on and the looseness showed.

            It is a `SplitRow` now, which is the shape this surface already uses
            wherever a label meets money — the cart's estimate, the receipt's own
            totals, the sent confirmation. **State on the left, amount on the
            right**: the pill and the one instruction that follows from it sit
            together as a sentence, and the figure gets the corner a guest's eye
            goes to on anything shaped like a receipt.

            The two freshness lines became one. "Updated 13:50" and "this may not
            be current" are the same subject, and stacking them spent two lines
            of a small card telling a guest twice how much to trust it. Joined
            with a `·`, and the second half still only appears while money is
            owed — a settled receipt is final and does not become less true when
            the stream drops.

            One shape for all four states (settled, refunded, voided, unpaid), so
            the card does not rearrange itself under a guest whose payment lands
            while they are looking at it. `role="status"` for the same reason.
          */}
          <Card role="status" tone="default">
            <Stack>
              <SplitRow>
                <span className="pk-stack q17-state">
                  {paid ? (
                    /* The screen's one Pandan. It comes from `isVerifiedPaid`,
                       which is `orderTotalTone` — the pill's tone is a literal
                       only in the sense that the branch it sits in already asked
                       `moneyTone.ts` the question. */
                    <StatusPill tone="verified">{t('q17.paid')}</StatusPill>
                  ) : refundedTotal > 0 ? (
                    <StatusPill>
                      {refundedTotal >= totals.paid_total_satang && totals.paid_total_satang > 0
                        ? t('q17.refunded')
                        : t('q17.partiallyRefunded')}
                    </StatusPill>
                  ) : order.state === 'voided' ? (
                    <StatusPill>{t('q14.orderVoided')}</StatusPill>
                  ) : (
                    /* **No "ยังไม่ได้ชำระ" pill.** The owner asked to drop it
                       (2026-08-17): the summary is a record of the order, and an
                       unpaid balance is the ordinary state at the table, not a
                       warning to badge. `paid`, `refunded` and `voided` still say
                       what they are — those are outcomes worth a word; "not yet
                       paid" is the default and needs none. The figure stays ink
                       (never green) until money is verified, so the money rule is
                       unchanged. `q17.payAtCounter` below still tells the guest
                       what to do. */
                    null
                  )}

                  {/* One line under the pill, and only where there is something
                      to do or to know. A voided ticket owes nothing, so
                      `q17.payAtCounter` would send a guest to the till to pay
                      for an order the venue cancelled — it asks them to speak to
                      someone instead. */}
                  {paid ? (
                    paidAtTime ? <span className="pk-muted">{t('q17.paidAt', {time: paidAtTime})}</span> : null
                  ) : order.state === 'voided' ? (
                    <span className="pk-muted">{t('common.askStaff')}</span>
                  ) : refundedTotal > 0 ? null : (
                    <span className="pk-muted">{t('q17.payAtCounter')}</span>
                  )}
                </span>

                {/*
                  Ink on every state including settled — the package tints the
                  label and leaves the amount alone. On the paid branch
                  `neutral` is written out rather than routed through
                  `orderTotalTone(order)`, because inside that branch the call is
                  a constant `'verified'` and passing it here would put the green
                  back. Hardcoding *away* from Pandan cannot break the money
                  rule: the rule forbids a device asserting a green it was not
                  told, and there is no tone this can be mistyped into that
                  invents one. The state the colour used to carry is in the pill
                  beside it and in this figure's own accessible name.
                */}
                <Money
                  satang={
                    refundedTotal > 0
                      ? refundedTotal
                      : !paid && order.state !== 'voided' && balanceDue > 0
                        ? balanceDue
                        : totals.total_satang
                  }
                  tone={refundedTotal > 0 ? 'refunded' : paid ? 'neutral' : orderTotalTone(order)}
                  size="lg"
                  ariaLabel={
                    paid
                      ? `${money(totals.total_satang)} · ${t('q17.paid')}`
                      : undefined
                  }
                />
              </SplitRow>

              {/*
                **No pay-online door here — owner directive, 2026-08-14.**

                An unpaid bill used to carry a link to Q8. It does not any more:
                for now this surface is an *ordering* system, and money is settled
                at the counter or by asking a member of staff.

                That is a smaller claim than the code could make — Q8 and Q9 work
                and the checkout route is real — and it is the honest one right
                now, because the half that does not work is the half a guest
                would notice: nothing in `pokpok-backend` writes
                `CheckoutSessionState.succeeded`, so a guest who paid online and
                one who abandoned the tab land in the same state, and neither
                settles the bill this screen is showing. `ROUTES.pay` still
                exists and Q8 is still reachable by URL; when settlement has an
                actor (`engineering/qr-redesign-backend-work.md`), this is one
                `ActionLink` to put back.
              */}

              {/* Freshness, in one line. `common.stale` only while money is
                  owed. */}
              {updatedAt || (stale && !paid) ? (
                <p className="pk-hint">
                  {[updatedAt ? t('common.updatedAt', {time: updatedAt}) : null,
                    stale && !paid ? t('common.stale') : null]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              ) : null}

              {processorDisclosed ? (
                <p className="pk-hint">{t('q17.processorDisclosure')}</p>
              ) : null}
            </Stack>
          </Card>

          {order.lines.length > 0 ? (
            <section>
              <SectionHeader
                title={t('q17.itemsHeading')}
                trailing={<span className="pk-muted">{t('common.items', {count: itemCount})}</span>}
              />
              {/* Rows on the page ground separated by the warm hairline, not
                  boxed in a card: the design draws the receipt list and the
                  status list the same way, and a card around a list of rules is
                  two borders doing one job. */}
              <ul className="q17-lines">
                {order.lines.map((line) => (
                  <li key={line.line_id} className="pk-ruled">
                    <LineRow line={line} />
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          <section>
            <Ledger order={order} />
          </section>

          {/*
            The `ร้าน` block is not in the design package, and it stays. The
            package is a single 390×844 prototype of one restaurant: every frame
            in it is implicitly that restaurant, so naming the venue inside a
            frame would have been redundant *in the prototype* and is not
            redundant in the product.

            What makes it earn the space here is the same property this whole
            screen is built around — it is the one surface a guest keeps. The URL
            outlives the session, the tab outlives the meal, and a guest who ate
            at two places today has two of these open. A receipt whose only
            identifiers are an order number and a total is not a receipt anybody
            can file, forward or argue with; the venue's name is the first thing a
            paper slip prints and the first thing a guest looks for when they open
            one again. It is also the only thing on the page that answers "whose
            counter do I take this to".

            It sits after the bill rather than in the header because it is
            provenance, not the headline: a guest reading top-down wants the state
            of their money first, and the guest who wants the name is looking for
            it, not glancing at it. It renders only when the session actually
            carries a display name — an empty heading over nothing is worse than
            no heading.
          */}
          {/*
            The venue block and the tax-invoice block were both here, and both
            were removed by owner directive on 2026-08-14.

            The venue: a guest reading this bill is sitting in the restaurant
            whose name is on the card they scanned, and the screen they reached
            it from names it in its top bar. A heading and a line repeating it at
            the foot of a receipt is furniture.

            The tax invoice: `q17.taxInvoiceAtCounter` said the request happens
            at the counter, which is true — the e-Tax engine is V2 and there is
            no endpoint to request one from. **Nothing about that changed here**;
            what changed is that this surface no longer volunteers it. A guest who
            needs a tax invoice in Thailand knows to ask, and a permanent section
            on every bill spends space on the rare case. If it comes back, it
            belongs in the payment block beside the amount, not as a third
            heading — and the copy is still in all three dictionaries.
          */}

          {/*
           * `q17.keepThisPage` — "you can open this page again any time" — used
           * to stand here unconditionally, and it is not true. Reading the order
           * needs a guest session token, and the `!sessionToken` branch above
           * answers `error.session` + `common.askStaff`: scan the QR again, ask
           * a member of staff. That is precisely the guest who took the promise
           * at its word and came back later. The token is table-scoped and
           * short-lived by design (`GuestSession.expires_at`), so "any time" is
           * a claim this surface cannot keep for anybody who has left the table,
           * and a receipt that over-promises its own durability produces the
           * support call it was written to prevent.
           *
           * Removing the claim is the whole fix: nothing else on the screen
           * depended on it, and a guest is not owed a sentence about durability
           * they cannot act on. The key stays in all three dictionaries, unused,
           * for the day there is a durable receipt link worth promising — the
           * same treatment `q12.queueLength` gets. A narrower replacement,
           * scoped to the life of the session, is in the report rather than
           * invented here.
           */}

          {/*
            INTEGRATION · the return leg of the Q14 ↔ Q17 pair.

            A guest crosses between "where is my food" and "what do I owe" more
            than once during a meal, and until integration this was a one-way
            trip: Q17 offered only the menu. The status link is shown only while
            the ticket is still open — a settled or voided order has no live
            status to go and look at.

            These repeat the top bar's back destination on purpose. The receipt
            is long, the page scrolls as a document, and the bar is off-screen by
            the time a guest has read the totals; a way out that requires
            scrolling back up is a way out a thumb does not find.
          */}
          {sessionToken ? (
            <div className="q17-ways-out">
              {ticketOpen ? (
                <ActionLink to={ROUTES.status} variant="secondary" block>
                  {t('q7.viewStatus')}
                </ActionLink>
              ) : null}
              <ActionLink to={ROUTES.menu} variant="tertiary" block>
                {t('common.viewMenu')}
              </ActionLink>
            </div>
          ) : null}

          <footer>
            <p className="pk-hint q17-attribution">{t('common.attribution')}</p>
          </footer>
        </div>
      </ScreenBody>
    </Screen>
  );
}

// --- pieces ----------------------------------------------------------------

/** A line the venue voided or the kitchen cancelled. Kept visible, never dropped. */
function isLineDropped(line: OrderLine): boolean {
  return line.voided || line.prep_state === 'cancelled';
}

function latestPaymentTime(payments: readonly PaymentSummary[]): string | null {
  let latest: string | null = null;
  for (const payment of payments) {
    const at = payment.settled_at ?? payment.created_at;
    if (!at) continue;
    if (latest === null || new Date(at).getTime() > new Date(latest).getTime()) latest = at;
  }
  return latest;
}

/**
 * One line of the receipt: `2× ผัดกะเพราหมู` on the left, what the venue charged
 * for it on the right, and everything that qualifies that figure underneath it
 * carrying **no figure of its own**.
 *
 * That last part is the fix, and it is worth the paragraph. Modifiers used to
 * print their own price delta in the same money column — a line at ฿90 with `฿15`
 * under it — and `line_total_satang` already contains that ฿15 (the server prices
 * the line as `(unit + Σ deltas) × quantity`). Two nested figures in one column
 * read as an addition: the guest totals ฿105, disagrees with the bill, and takes
 * it to the counter. It is the same defect ADR-0009 names for inclusive VAT, one
 * level down, and it gets the same answer: **a breakdown that appears to sum to
 * more than the total is a receipt a guest argues with.**
 *
 * The modifiers stay, by name, because a receipt's job is to say what arrived —
 * and `modifier.quantity` leads the name when it is more than one, since "two
 * extra eggs" is a fact about the plate. What an extra *cost* was a decision the
 * guest already made on Q4, where the delta was shown beside the choice; it is
 * not a number this screen owes them, and it is not a number that can be shown
 * here without also showing the base price it modifies, which the contract does
 * not carry separately once modifiers are folded into `unit_price_satang`.
 */
function LineRow({line}: {line: OrderLine}) {
  const {t, ln} = useI18n();
  const dropped = isLineDropped(line);
  const modifiers = line.modifiers ?? [];

  return (
    <div className="q17-line">
      <SplitRow>
        {/* Colour never carries the cancellation on its own — the pill below
            says it in words, and the strike-through is a second, non-colour
            carrier for a guest reading this in direct sunlight. */}
        <span className={cx('pk-strong', dropped && 'q17-dropped')}>
          <span className="pk-money">{line.quantity}×</span> {ln(line.name_snapshot)}
        </span>
        <Money satang={line.line_total_satang} tone={dropped ? 'refunded' : 'neutral'} />
      </SplitRow>

      {dropped ? <StatusPill>{t('q14.stateCancelled')}</StatusPill> : null}

      {modifiers.length > 0 ? (
        <p className="pk-muted">
          {modifiers
            .map((m) => {
              const name = ln(m.name_snapshot);
              return m.quantity > 1 ? `${m.quantity}× ${name}` : name;
            })
            .join(' · ')}
        </p>
      ) : null}

      {line.note ? <p className="pk-muted">{line.note}</p> : null}
    </div>
  );
}

interface LedgerLine {
  key: string;
  label: string;
  amountSatang: number;
  tone: MoneyTone;
  emphasis?: boolean;
}

/**
 * The bill.
 *
 * Three rules shape this and none is negotiable:
 *
 * 1. **Under inclusive VAT the tax is already inside the total** (ADR-0009), so
 *    it is a note under the total, never a row added into it. A breakdown that
 *    sums to more than the total is a receipt a guest argues with.
 * 2. **A row is only shown if it can be labelled truthfully.** `common.vatAdded`
 *    and `common.serviceCharge` both need a rate, and the contract marks both
 *    rate fields optional. When a non-zero charge arrives with no rate, the
 *    itemised breakdown is dropped entirely and only the server's total is
 *    shown — a total with no breakdown is honest; a breakdown with an invented
 *    or missing line is not.
 * 3. **No figure is printed twice under two labels.** On an untouched unpaid
 *    bill the balance due *is* the total, and stating it again below the tear
 *    asks the guest to reconcile a number against itself. The settlement row is
 *    shown when it carries a fact the total does not — see the balance row
 *    below.
 *
 * The design's receipt tear — the one dashed rule in the whole package — sits
 * directly above the total, which is exactly where those two rules meet:
 * everything above it explains the figure, and the figure is what the venue
 * charged. The VAT note goes **below** the tear for the same reason; putting it
 * with the rows above would put it back in the column of things being added up.
 */
function Ledger({order}: {order: Order}) {
  const {t} = useI18n();
  const totals = order.totals;

  const vat = totals.vat_satang;
  const serviceCharge = totals.service_charge_satang;
  const tip = totals.tip_satang ?? 0;
  const vatRate = totals.vat_rate_bps;
  const serviceRate = totals.service_charge_rate_bps;
  const inclusiveVat = totals.vat_mode === 'inclusive';
  const knownVatMode = inclusiveVat || totals.vat_mode === 'exclusive';

  const canLabelBreakdown =
    (vat === 0 || (knownVatMode && vatRate !== undefined)) &&
    (serviceCharge === 0 || serviceRate !== undefined);

  const rows: LedgerLine[] = [];

  if (canLabelBreakdown) {
    rows.push({
      key: 'subtotal',
      label: t('common.subtotal'),
      amountSatang: totals.subtotal_satang,
      tone: 'neutral',
    });
    if (totals.discount_total_satang !== 0) {
      rows.push({
        key: 'discount',
        label: t('common.discount'),
        amountSatang: totals.discount_total_satang,
        tone: 'neutral',
      });
    }
    if (serviceCharge !== 0 && serviceRate !== undefined) {
      rows.push({
        key: 'service',
        label: t('common.serviceCharge', {rate: formatBasisPoints(serviceRate)}),
        amountSatang: serviceCharge,
        tone: 'neutral',
      });
    }
    if (vat !== 0 && !inclusiveVat && vatRate !== undefined) {
      rows.push({
        key: 'vat',
        label: t('common.vatAdded', {rate: formatBasisPoints(vatRate)}),
        amountSatang: vat,
        tone: 'neutral',
      });
    }
    if (totals.rounding_adjustment_satang !== 0) {
      rows.push({
        key: 'rounding',
        label: t('common.rounding'),
        amountSatang: totals.rounding_adjustment_satang,
        tone: 'neutral',
      });
    }
    if (tip !== 0) {
      rows.push({key: 'tip', label: t('common.tip'), amountSatang: tip, tone: 'neutral'});
    }
  }

  const after: LedgerLine[] = [];
  if (totals.paid_total_satang > 0) {
    // `paid_total_satang` counts only succeeded payments server-side, but this
    // screen still earns Pandan through `paymentTone` rather than through a
    // number it read. No payment record on hand means no verified colour — and
    // the tone is the *payment's* own, never the literal, so the only way this
    // row goes green is a `succeeded` record on the wire.
    const verified = (order.payments ?? []).find((p) => paymentTone(p) === 'verified');
    after.push({
      key: 'paid',
      label: t('q17.paid'),
      amountSatang: totals.paid_total_satang,
      tone: verified ? paymentTone(verified) : 'pending',
    });
  }
  if (totals.refunded_total_satang > 0) {
    after.push({
      key: 'refunded',
      label:
        totals.refunded_total_satang >= totals.paid_total_satang
          ? t('q17.refunded')
          : t('q17.partiallyRefunded'),
      amountSatang: totals.refunded_total_satang,
      tone: 'refunded',
    });
  }
  /*
   * The balance row, and the condition that keeps it off an ordinary bill.
   *
   * On a plain unpaid ticket `balance_due_satang === total_satang` by definition
   * — nothing has been taken off it — so printing the row put the same ฿185
   * under `รวมทั้งสิ้น` and again under `ยอดค้างชำระ` a few pixels below, with a
   * rule drawn between them announcing a second group. Two identical figures with
   * different labels is an invitation to reconcile, and this is the screen whose
   * own doctrine (see `LineRow`, ADR-0009) is that a guest must never be handed
   * arithmetic that appears to say two things about one amount. The guest who
   * adds them gets ฿370 and takes it to the counter; the guest who does not still
   * pauses to work out why the bill said it twice.
   *
   * So the row is suppressed **only in the case where it is a restatement** —
   * nothing paid, nothing refunded, and the balance equal to the total. The
   * moment either is false the two numbers carry different facts and the row
   * comes straight back:
   *
   *   * a part-payment (฿100 of ฿185 taken) — `paid_total_satang > 0`, and
   *     `ยอดค้างชำระ ฿85` is the only place the remainder is stated;
   *   * a refund that reopened a balance — `refunded_total_satang > 0`;
   *   * any server arithmetic that makes the balance differ from the total on its
   *     own (a deposit applied elsewhere, a rounding adjustment landing after
   *     settlement) — the equality test catches those without this screen having
   *     to know what caused them.
   *
   * Not dropping the row outright matters: the case it exists for is exactly the
   * one a guest cannot reconstruct from the rest of the page.
   *
   * A voided order owes nothing, whatever the arithmetic left behind says.
   */
  const nothingSettled = totals.paid_total_satang === 0 && totals.refunded_total_satang === 0;
  const balanceRestatesTotal =
    nothingSettled && totals.balance_due_satang === totals.total_satang;
  if (totals.balance_due_satang > 0 && order.state !== 'voided' && !balanceRestatesTotal) {
    after.push({
      key: 'balance',
      label: t('q17.balanceDue'),
      amountSatang: totals.balance_due_satang,
      tone: 'pending',
    });
  }

  return (
    <div className="q17-ledger">
      {rows.map((row) => (
        <LedgerAmount key={row.key} row={row} />
      ))}

      <LedgerAmount
        row={{
          key: 'total',
          label: t('common.total'),
          amountSatang: totals.total_satang,
          tone: orderTotalTone(order),
          emphasis: true,
        }}
      />

      {inclusiveVat && vat !== 0 && vatRate !== undefined ? (
        <p className="pk-muted">{t('common.vatIncluded', {rate: formatBasisPoints(vatRate)})}</p>
      ) : null}

      {after.length > 0 ? (
        <div className="q17-settlement">
          {after.map((row) => (
            <LedgerAmount key={row.key} row={row} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * A ledger row. The total is emphasised by **weight, not size** — the big figure
 * on this screen is the one in the card at the top, and a second `lg` amount
 * further down would make the guest look twice to find out which one is the bill.
 */
function LedgerAmount({row}: {row: LedgerLine}) {
  return (
    <SplitRow className={cx(row.emphasis && 'q17-ledger-total')}>
      <span className={row.emphasis ? 'pk-strong' : 'pk-muted'}>{row.label}</span>
      <Money satang={row.amountSatang} tone={row.tone} />
    </SplitRow>
  );
}

/**
 * Loading, at the geometry of the receipt so nothing reflows when it lands.
 *
 * The design package draws no loading state anywhere — not one spinner in 485
 * lines — so this is built from what `index.html` already paints before the
 * bundle arrives: the same grey, the same bar shape, static and never shimmering.
 * The title is real from the first frame, because it is the one thing on this
 * page that is known before the request returns.
 */
function ReceiptSkeleton({busy}: {busy: boolean}) {
  const {t} = useI18n();
  return (
    <Screen>
      <TopBar trailing={<LanguageToggle variant="link" />} />
      <ScreenBody>
        <div className="q17">
          <header className="q17-header">
            <h1 className="pk-title">{t('q17.title')}</h1>
          </header>
          {/* Announced once, at the region that will hold the content. Thirty
              announcing bars is a screen reader reading static. */}
          <div role="status" aria-busy={busy} aria-label={t('common.loading')}>
            <Card>
              <Stack>
                <SkeletonBar width="96px" />
                <SkeletonBar width="160px" height="28px" />
                <SkeletonBar width="128px" />
              </Stack>
            </Card>
            {[0, 1, 2].map((i) => (
              <SkeletonRow key={i}>
                <SkeletonBar />
                <SkeletonBar width="64px" />
              </SkeletonRow>
            ))}
          </div>
        </div>
      </ScreenBody>
    </Screen>
  );
}

/**
 * A dead end that tells the guest what to do next, in one line (foundations §9).
 * Never shows the API's English `message` — the contract is explicit that it is
 * for operators, and this is the most public surface in the product.
 *
 * `warning` rather than `error`: a receipt that cannot be read is Palm Sugar,
 * not Dried Chili — nothing is broken for the guest, the venue still has the
 * order, and the instruction is a human at the counter. Dried Chili is Q19's, for
 * a QR that genuinely cannot be used.
 */
function Notice({
  title,
  body,
  onRetry,
  retryLabel,
}: {
  title: string;
  body?: string;
  onRetry?: () => void;
  retryLabel?: string;
}): ReactNode {
  const {t} = useI18n();
  return (
    <Screen>
      {/* Every state of this screen, including the failures. A guest who cannot
          read "we could not find that order" is helped by nothing else here. */}
      <TopBar trailing={<LanguageToggle variant="link" />} />
      <ScreenBody center>
        <TerminalNotice tone="warning" glyph="!" title={title}>
          {body ? <p className="pk-muted">{body}</p> : null}
        </TerminalNotice>
      </ScreenBody>
      <ScreenFooter bordered={false}>
        {/* The screen's one solid red, and only when there is genuinely
            something forward to do: a retriable error. A red button that
            re-fetches an order that does not exist is a promise it cannot keep. */}
        {onRetry && retryLabel ? (
          <Button variant="primary" onClick={onRetry}>
            {retryLabel}
          </Button>
        ) : null}
        <p className="pk-hint q17-attribution">{t('common.attribution')}</p>
      </ScreenFooter>
    </Screen>
  );
}
